import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, Phone, MapPin, Send, Clock, MessageCircle, Navigation,
  Instagram, Facebook, Youtube, Linkedin, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [settings, setSettings] = useState({
    social_media: {},
    contact_info: {
      primary_email: '',
      primary_phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      office_hours: 'Mon - Sat: 9:00 AM - 6:00 PM',
      google_maps_url: ''
    }
  });

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/all`);
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, []);

  const { social_media, contact_info } = settings;

  // Build full address
  const fullAddress = [
    contact_info.address_line1,
    contact_info.address_line2,
    contact_info.city,
    contact_info.state,
    contact_info.pincode,
    contact_info.country
  ].filter(Boolean).join(', ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Message sent! We\'ll get back to you soon.');
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setLoading(false);
  };

  const contactInfo = [
    { 
      icon: Phone, 
      title: 'Phone', 
      value: contact_info.primary_phone || 'Not available', 
      href: contact_info.primary_phone ? `tel:${contact_info.primary_phone.replace(/\s/g, '')}` : '#', 
      gradient: 'from-emerald-500 to-teal-500' 
    },
    { 
      icon: Mail, 
      title: 'Email', 
      value: contact_info.primary_email || 'Not available', 
      href: contact_info.primary_email ? `mailto:${contact_info.primary_email}` : '#', 
      gradient: 'from-blue-500 to-cyan-500' 
    },
    { 
      icon: MapPin, 
      title: 'Address', 
      value: fullAddress || 'Not available', 
      href: contact_info.google_maps_url || '#', 
      gradient: 'from-purple-500 to-pink-500' 
    },
    { 
      icon: Clock, 
      title: 'Office Hours', 
      value: contact_info.office_hours || 'Mon - Sat: 9:00 AM - 6:00 PM', 
      href: '#', 
      gradient: 'from-orange-500 to-amber-500' 
    }
  ];

  // Social links with icons
  const socialLinks = [
    { key: 'instagram', icon: Instagram, name: 'Instagram', color: 'hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500' },
    { key: 'facebook', icon: Facebook, name: 'Facebook', color: 'hover:bg-blue-600' },
    { key: 'youtube', icon: Youtube, name: 'YouTube', color: 'hover:bg-red-600' },
    { key: 'linkedin', icon: Linkedin, name: 'LinkedIn', color: 'hover:bg-blue-700' }
  ].filter(s => social_media[s.key]);

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge className="mb-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0">
            <MapPin className="w-4 h-4 mr-2" />
            Contact Us
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Get In <span className="text-gradient-green">Touch</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions about our programs? Want to visit our campus? We&apos;d love to hear from you.
          </p>
        </motion.div>

        {/* Contact Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {contactInfo.map((info, i) => (
            <Card key={i} className={`${cardClass} hover:border-primary/50 transition-colors`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${info.gradient} flex items-center justify-center flex-shrink-0`}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold mb-1">{info.title}</p>
                    {info.href !== '#' ? (
                      <a 
                        href={info.href} 
                        target={info.href.startsWith('http') ? '_blank' : undefined}
                        rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors break-words"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">{info.value}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className={cardClass}>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-2">Send us a message</h2>
                <p className="text-muted-foreground mb-6">Fill out the form and our team will get back to you within 24 hours.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 9999999999"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="How can we help you?"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      rows={5}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full btn-gradient h-12 text-base" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Quick Help */}
            {contact_info.primary_phone && (
              <Card className={`mt-6 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/20 ${cardClass}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Need quick answers?</h3>
                      <p className="text-sm text-muted-foreground">
                        Call us directly at <a href={`tel:${contact_info.primary_phone.replace(/\s/g, '')}`} className="text-primary font-medium">{contact_info.primary_phone}</a>
                        {contact_info.whatsapp && (
                          <> or <a href={`https://wa.me/${contact_info.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 font-medium">WhatsApp us</a></>
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Google Map */}
            <Card className={`${cardClass} overflow-hidden`}>
              <CardContent className="p-0">
                <div className="relative">
                  {contact_info.google_maps_url ? (
                    <iframe
                      src={contact_info.google_maps_url.includes('embed') ? contact_info.google_maps_url : `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3667.0!2d77.08!3d23.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${encodeURIComponent(fullAddress)}!5e0!3m2!1sen!2sin!4v1704700000000`}
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Visual Institute of Arts Location"
                      className="rounded-t-xl"
                    />
                  ) : (
                    <div className="h-[400px] bg-muted flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Map location not configured</p>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-gray-800 shadow-lg">
                      <MapPin className="w-3 h-3 mr-1 text-red-500" />
                      Visual Institute of Arts
                    </Badge>
                  </div>
                </div>
                <div className="p-4 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">Visual Institute of Arts</h3>
                      <p className="text-sm text-muted-foreground">{fullAddress || 'Address not available'}</p>
                    </div>
                    {contact_info.google_maps_url && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <a href={contact_info.google_maps_url} target="_blank" rel="noopener noreferrer">
                          <Navigation className="w-4 h-4 mr-2" />
                          Get Directions
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Visit Us CTA */}
            <Card className={`bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20 ${cardClass}`}>
              <CardContent className="p-6">
                <h3 className="font-bold text-xl mb-2">Visit Our Campus</h3>
                <p className="text-muted-foreground mb-4">
                  Experience our state-of-the-art facilities and meet our faculty. Schedule a campus tour today!
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button className="btn-gradient">
                    Schedule a Tour
                  </Button>
                  {contact_info.primary_phone && (
                    <Button variant="outline" asChild>
                      <a href={`tel:${contact_info.primary_phone.replace(/\s/g, '')}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            {socialLinks.length > 0 && (
              <Card className={cardClass}>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Connect With Us</h3>
                  <div className="flex gap-3 flex-wrap">
                    {socialLinks.map((social) => (
                      <Button 
                        key={social.key} 
                        variant="outline" 
                        size="sm" 
                        className={`${social.color} hover:text-white transition-all`}
                        asChild
                      >
                        <a href={social_media[social.key]} target="_blank" rel="noopener noreferrer">
                          <social.icon className="w-4 h-4 mr-2" />
                          {social.name}
                        </a>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
